/**
 * Connection strength check for model download.
 * Uses Network Information API (effectiveType, downlink, rtt) when available
 * to warn users when their connection may be too weak for a reliable model download.
 */

const WEAK_DOWNLINK_MBPS = 0.5;   // Consider weak if estimated speed < 0.5 Mbps
const WEAK_RTT_MS = 500;          // Consider weak if round-trip time > 500 ms
const WEAK_EFFECTIVE_TYPES = ['2g', 'slow-2g', '3g'];

/**
 * Check if the current connection is likely too weak for a reliable model download.
 * Uses navigator.connection when available (Chrome, some Android). Safari/iOS may not support it.
 * @returns {{ weak: boolean, message: string }}
 */
export function getConnectionStrength() {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (!conn) {
    return { weak: false, message: '' };
  }

  const effectiveType = (conn.effectiveType || '').toLowerCase();
  const downlink = conn.downlink != null ? conn.downlink : Infinity; // Mbps
  const rtt = conn.rtt != null ? conn.rtt : 0; // ms
  const saveData = conn.saveData === true;

  if (saveData) {
    return {
      weak: true,
      message: 'Data saver is on. Model download may fail or be slow. Turn it off for a better experience.',
    };
  }

  if (WEAK_EFFECTIVE_TYPES.includes(effectiveType)) {
    return {
      weak: true,
      message: 'Your connection appears weak (2G/3G/slow). Model download may be slow or fail. We recommend a stable Wi‑Fi connection.',
    };
  }

  if (downlink < WEAK_DOWNLINK_MBPS && downlink > 0) {
    return {
      weak: true,
      message: 'Your connection speed seems low. Model download may take a long time or fail. We recommend a stronger Wi‑Fi connection.',
    };
  }

  if (rtt > WEAK_RTT_MS && rtt < 9999) {
    return {
      weak: true,
      message: 'Your connection seems unstable (high latency). Model download may fail. Try moving closer to the router or use a stable Wi‑Fi.',
    };
  }

  return { weak: false, message: '' };
}

/**
 * Check if an error is likely due to network/connection (for showing connection-related error message).
 * @param {Error} error
 * @returns {boolean}
 */
export function isNetworkRelatedError(error) {
  if (!error || !error.message) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed') ||
    msg.includes('load failed') ||
    msg.includes('timeout') ||
    msg.includes('err_network') ||
    (error.name === 'TypeError' && msg.includes('fetch'))
  );
}
